/**
 * "Gerar OC" a partir da requisição (padrão Transition Form): aparece quando
 * o comprador arrasta para "Aguardando entrega" sem OC, ou pelo botão do
 * drawer. Escolhe fornecedor, prazo e os itens abertos com produto; o preço
 * vem da cotação vencedora, do último preço do fornecedor ou é digitado.
 */
import React, { useEffect, useMemo, useState } from "react";
import { FiAlertCircle } from "react-icons/fi";

import Checkbox from "../../../components/Checkbox";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { criarOcDeRequisicao, listarFornecedores } from "../../../services/compras";
import { formatarReaisBR, parseDinheiro } from "../../../utils/dinheiro";
import { fmtQt } from "../../RevisorFerragens/lib/documento";
import { Mono } from "../_shared/quadro/styles";
import { Rotulo } from "./ui";

const CAMPO = {
  height: 36, padding: "0 10px", borderRadius: 8, border: "1px solid var(--border)",
  background: "var(--surface)", color: "var(--ink)", font: "inherit", fontSize: 13, width: "100%", boxSizing: "border-box",
};

export default function GerarOcPanel({ open, req, onClose, onGerada }) {
  const { request } = useAuth();
  const [fornecedores, setFornecedores] = useState([]);
  const [fornecedor, setFornecedor] = useState("");
  const [prazo, setPrazo] = useState("");
  const [obs, setObs] = useState("");
  const [sel, setSel] = useState(() => new Set());
  const [valores, setValores] = useState({});
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const abertos = useMemo(
    // So item APROVADO entra em OC (o backend recusa os demais): item que
    // chegou depois da aprovacao nao pega carona na aprovacao dos outros.
    () => (req?.itens || []).filter((it) => it.produto && it.situacao === "APROVADO"),
    [req],
  );

  useEffect(() => {
    if (!open) return;
    setErro(null);
    setFornecedor("");
    setPrazo("");
    setObs("");
    setSel(new Set(abertos.map((it) => it.id)));
    setValores(Object.fromEntries(abertos.map((it) => {
      const venc = (it.cotacoes || []).find((c) => c.vencedora);
      return [it.id, venc?.valor_unitario ? String(venc.valor_unitario).replace(".", ",") : ""];
    })));
    listarFornecedores(request, { pageSize: 500, ativo: true })
      .then((p) => setFornecedores(Array.isArray(p) ? p : p?.results || []))
      .catch(() => setFornecedores([]));
    // Sugestão: o fornecedor vencedor mais frequente entre os itens.
    const contagem = {};
    abertos.forEach((it) => {
      const venc = (it.cotacoes || []).find((c) => c.vencedora);
      if (venc) contagem[venc.fornecedor] = (contagem[venc.fornecedor] || 0) + 1;
    });
    const melhor = Object.entries(contagem).sort((a, b) => b[1] - a[1])[0];
    if (melhor) setFornecedor(melhor[0]);
  }, [open, abertos, request]);

  const total = useMemo(() => abertos.reduce((acc, it) => {
    if (!sel.has(it.id)) return acc;
    const v = parseDinheiro(valores[it.id] || "0") || 0;
    return acc + v * Number(it.qtd_aprovada || it.qtd_solicitada || 0);
  }, 0), [abertos, sel, valores]);

  async function gerar() {
    if (!fornecedor) { setErro("Escolha o fornecedor."); return; }
    if (!sel.size) { setErro("Marque ao menos um item."); return; }
    setErro(null);
    setSalvando(true);
    try {
      const vals = {};
      abertos.forEach((it) => {
        if (!sel.has(it.id)) return;
        const v = parseDinheiro(valores[it.id] || "");
        if (v !== null && v !== undefined && !Number.isNaN(v)) vals[it.id] = v.toFixed(4);
      });
      const oc = await criarOcDeRequisicao(request, req.id, {
        fornecedor, prazo_entrega: prazo || null, observacao: obs, valores: vals, itens: Array.from(sel),
      });
      await onGerada?.(oc);
    } catch (err) {
      setErro(err?.payload?.detalhe || err?.message || "Falha ao gerar a OC.");
    } finally {
      setSalvando(false);
    }
  }

  const footer = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 12, color: "var(--ink-3)" }}>Total <Mono $size={13}>{formatarReaisBR(total)}</Mono></span>
      <span style={{ flex: 1 }} />
      <button type="button" className="btn btn--secondary" onClick={onClose}>Cancelar</button>
      <button type="button" className="btn btn--primary" onClick={gerar} disabled={salvando}>
        {salvando ? "Gerando…" : "Gerar OC"}
      </button>
    </div>
  );

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title={`Gerar OC · ${req?.numero || ""}`}
      subtitle="Uma ordem de compra para um fornecedor com os itens marcados."
      width="min(94vw, 560px)"
      footer={footer}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 150px", gap: 12 }}>
          <div>
            <Rotulo>Fornecedor</Rotulo>
            <select value={fornecedor} onChange={(e) => setFornecedor(e.target.value)} style={CAMPO}>
              <option value="">— Escolha —</option>
              {fornecedores.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </div>
          <div>
            <Rotulo>Prazo de entrega</Rotulo>
            <input type="date" value={prazo} onChange={(e) => setPrazo(e.target.value)} style={CAMPO} />
          </div>
        </div>
        <div>
          <Rotulo>Itens ({sel.size} de {abertos.length})</Rotulo>
          <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
            {abertos.length === 0 ? (
              <div style={{ padding: 16, fontSize: 13, color: "var(--ink-3)" }}>Nenhum item aprovado com produto de catálogo. Aprove a requisição para gerar a OC.</div>
            ) : abertos.map((it) => (
              <div key={it.id} style={{ display: "grid", gridTemplateColumns: "24px minmax(0,1fr) 70px 120px", gap: 10, alignItems: "center", padding: "8px 12px", borderBottom: "1px solid var(--border-subtle)" }}>
                <Checkbox checked={sel.has(it.id)} onChange={() => setSel((s) => { const n = new Set(s); if (n.has(it.id)) n.delete(it.id); else n.add(it.id); return n; })} aria-label={`Incluir ${it.produto_nome}`} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.produto_nome}</div>
                  <div style={{ fontSize: 11, color: "var(--ink-3)" }}><Mono $size={11}>{it.produto_codigo}</Mono> · {it.produto_unidade}</div>
                </div>
                <Mono style={{ textAlign: "right" }}>×{fmtQt(it.qtd_aprovada || it.qtd_solicitada)}</Mono>
                <input
                  value={valores[it.id] || ""}
                  onChange={(e) => setValores((v) => ({ ...v, [it.id]: e.target.value }))}
                  placeholder="Unit. R$"
                  inputMode="decimal"
                  style={{ ...CAMPO, height: 32, fontFamily: "var(--font-mono)", textAlign: "right" }}
                  aria-label={`Valor unitário de ${it.produto_nome}`}
                />
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 6 }}>
            Vazio = último preço do fornecedor para o produto (ou zero). Item de terceiro não entra em OC.
          </div>
        </div>
        <div>
          <Rotulo>Observação</Rotulo>
          <input value={obs} onChange={(e) => setObs(e.target.value)} maxLength={300} style={CAMPO} placeholder="Opcional" />
        </div>
        {erro ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--danger-text)", fontSize: 13 }}>
            <FiAlertCircle size={14} /> {erro}
          </div>
        ) : null}
      </div>
    </SlideOverPanel>
  );
}
